import React from "react";
import styled from "styled-components";
import { colors } from "../../utils";
import { radius, shadow, spacing, zIndex } from "../../theme/tokens";

/**
 * SkipToContent — the "skip to main content" bypass link (WCAG 2.1 SC 2.4.1
 * "Bypass Blocks", Level A).
 *
 * Problem it fixes (a11y measurement A35, re-measured on the DEPLOYED build
 * web-stg 2026-07-28 at 375x812, account 12145): every surface reports
 * `skipLinks = 0` — there is no bypass link anywhere in the bundle. A keyboard
 * or screen-reader user must Tab through the whole header (logo, search,
 * feedback, capture, chat, follower-feed, notifications, profile menu) on
 * EVERY page before reaching the actual page content.
 *
 * Deliberately conservative, to respect the legacy app:
 *  - Purely ADDITIVE. No existing markup, style or behaviour changes.
 *  - The target is a plain `#main-content` wrapper, NOT a `<main>` landmark.
 *    Three pages (ViewWheelV2, RendererV2Compare, RendererV2Interaction)
 *    already render their OWN `<main>`; introducing an app-level `<main>` would
 *    nest landmarks and create two mains on those routes, which is worse than
 *    having none. Adding per-page `<main>` landmarks (SC 1.3.1) is a separate,
 *    larger unit and is intentionally NOT bundled here.
 *  - Rendered as the FIRST child of `.App`, so it is the first thing Tab
 *    reaches — ahead of the toast container and every header control.
 *  - Off-screen until focused (never `display:none`, which would remove it from
 *    the tab order entirely and defeat the purpose).
 *  - Moves focus explicitly rather than relying on the `href="#..."` fragment:
 *    in a client-side-routed SPA the hash is owned by the router, and a bare
 *    fragment jump does not reliably move FOCUS (only scroll position).
 */

export const MAIN_CONTENT_ID = "main-content";

const Link = styled.a`
  position: fixed;
  top: ${spacing.xs}px;
  left: ${spacing.xs}px;
  /* Off-screen, but still focusable and still in the tab order. */
  transform: translateY(calc(-100% - ${spacing.lg}px));
  z-index: ${zIndex.tooltip};

  display: inline-flex;
  align-items: center;
  min-height: 44px; /* WCAG 2.5.5 touch target */
  padding: 0 ${spacing.md}px;
  border-radius: ${radius.sm}px;

  /* PrimaryPurple800 on white text = ~8.6:1 — passes AA (and AAA for large).
     The lighter brand PrimaryPurple (#9B5AF8) is only 4.03:1 on white and is
     deliberately NOT used here. */
  background-color: ${colors.PrimaryPurple800};
  color: ${colors.white};
  font-weight: 600;
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
  box-shadow: ${shadow.e2};

  transition: transform 150ms ease-out;

  &:focus,
  &:focus-visible {
    transform: translateY(0);
    outline: none;
    box-shadow: ${shadow.focus}, ${shadow.e2};
  }

  /* Honour the OS setting: snap into place instead of sliding. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export default function SkipToContent({ targetId = MAIN_CONTENT_ID, children = "Skip to main content" }) {
  const onActivate = (e) => {
    const target = typeof document !== "undefined" && document.getElementById(targetId);
    if (!target) return; // let the browser do whatever it would have done
    e.preventDefault();
    // The wrapper carries tabIndex={-1} so it can receive programmatic focus.
    try {
      target.focus({ preventScroll: true });
    } catch (err) {
      target.focus();
    }
    if (typeof target.scrollIntoView === "function") {
      // Always instant: a "skip" that animates defeats its own purpose.
      target.scrollIntoView({ block: "start", behavior: "auto" });
    }
  };

  return (
    <Link
      href={`#${targetId}`}
      data-testid="skip-to-content"
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") onActivate(e);
      }}
    >
      {children}
    </Link>
  );
}
